import { useEffect, useState } from 'react';
import { Button } from './ui';

// No 0/O, 1/l/I: these are read aloud and typed by hand when a login is handed over.
const ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
// Longer than the server's minimum of 8, but still short enough to read out and type;
// at 57 symbols a character it is about 70 bits - far past guessable, with argon2id
// making every guess costly.
const LENGTH = 12;

/**
 * A new password: 12 characters from a 57-character alphabet with no look-alikes.
 *
 * crypto.getRandomValues with rejection sampling, so every character is equally likely;
 * Math.random would be predictable, and a plain `% 57` would favour the first few letters.
 */
export function generatePassword(): string {
  const limit = 256 - (256 % ALPHABET.length);
  const chars: string[] = [];
  while (chars.length < LENGTH) {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    for (const byte of bytes) {
      if (byte < limit && chars.length < LENGTH) chars.push(ALPHABET[byte % ALPHABET.length]);
    }
  }
  return chars.join('');
}

/**
 * A password input with Generate, Show and Copy.
 *
 * Generating shows the password - whoever made it has to read it or copy it to hand it
 * over - and Copy is there so it never has to be retyped. It is still an ordinary input:
 * typing one by hand works as before.
 */
export function PasswordField({
  value,
  onChange,
  autoComplete = 'new-password',
  allowGenerate = true,
  autoFocus = false,
  onGenerated,
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  allowGenerate?: boolean;
  autoFocus?: boolean;
  /** Told the value when Generate made it, e.g. to fill a confirmation box. */
  onGenerated?: (value: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');

  // "Copied" describes the value that was copied; once it changes, it no longer does.
  useEffect(() => setCopied('idle'), [value]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied('copied');
    } catch {
      // The clipboard API needs a secure page and permission; say so rather than
      // pretend. The field is visible by now, so it can be selected by hand.
      setVisible(true);
      setCopied('failed');
    }
  }

  return (
    <div>
      <input
        type={visible ? 'text' : 'password'}
        required
        minLength={8}
        maxLength={200}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        spellCheck={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={visible ? 'font-mono' : undefined}
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {allowGenerate && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              const generated = generatePassword();
              onChange(generated);
              onGenerated?.(generated);
              setVisible(true);
            }}
          >
            Generate
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={() => setVisible((v) => !v)}>
          {visible ? 'Hide' : 'Show'}
        </Button>
        <Button type="button" variant="ghost" disabled={!value} onClick={copy}>
          Copy
        </Button>
        {copied === 'copied' && <span className="text-xs text-emerald-300">Copied</span>}
        {copied === 'failed' && (
          <span className="text-xs text-amber-300">Could not copy - select it and copy by hand</span>
        )}
      </div>
    </div>
  );
}
